import { useAuth } from '@agentnet/auth';
import { GetFileInfoProps } from 'api/file/file-api';
import { updateFincenTransferorAPI } from 'api/fincen-report-api';
import { FileDataContext, FileDataContextInterface } from 'hooks/FileDataContext';
import { FincenReportContext, FincenReportContextInterface } from 'hooks/FincenReportContext';
import useAsync from 'hooks/useAsync';
import { useAutoSaveNavigation, useFincenAutoSave } from 'hooks/useAutoSave';
import { useNavigationLoading } from 'hooks/useNavigationLoading';
import { FC, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Redirect, useHistory, useParams } from 'react-router-dom';
import BottomAppBar from 'ui-kit/components/bottomAppBar/BottomAppBar';
import LoadingSpinner from 'ui-kit/components/LoadingSpinner';
import useSnackBars from 'ui-kit/components/notification/useSnackbars';
import { AsyncStatus, MAX_ITEMS_LIMIT } from '../constants';
import { Transferor } from '../types';
import { getInitialTransferorData } from './models/transferor';
import TransferorInformationContent from './TransferorInformationContent';

const TransferorInformationContainer: FC = () => {
  const { fileId } = useParams<{ fileId: string }>();
  const { addSnackbarMessage } = useSnackBars();
  const history = useHistory();
  const { getAccessToken } = useAuth();

  const fileDataCtx = useContext(FileDataContext) as FileDataContextInterface;
  const { fileData } = fileDataCtx;

  // Load file data when lost on refresh
  useEffect(() => {
    if (fileId && fileDataCtx && !fileDataCtx.fileData?.fileId && fileDataCtx.loadFileData) {
      const fileProps: GetFileInfoProps = {
        fileId,
      };
      fileDataCtx.loadFileData(fileProps);
    }
  }, [fileId, fileDataCtx]);

  const fincenReportCtx = useContext<FincenReportContextInterface | null>(FincenReportContext);
  const { fincenReport, fincenReportStatus, updateTransferor } = fincenReportCtx ?? {};

  const [transferors, setTransferors] = useState<Transferor[]>([
    {
      ...getInitialTransferorData(),
      id: `transferor_${Date.now()}`,
    },
  ]);
  const [formSubmitted, setFormSubmitted] = useState<boolean>(false);
  const [shouldNavigateOnComplete, setShouldNavigateOnComplete] = useState(false);
  const isNavigating = useNavigationLoading();
  const hasLoadedFromContextRef = useRef(false);
  const isInitialLoadRef = useRef(true);
  const justSavedRef = useRef(false);

  const handleAutoSave = useCallback(async (): Promise<void> => {
    if (!fincenReport) return;
    if (fincenReport.reportStatusCdId === 9302) return;
    if (isInitialLoadRef.current) {
      return;
    }
    // Update context first so it has the latest data when returning to this page
    if (updateTransferor) {
      updateTransferor(transferors);
    }
    const token = await getAccessToken();
    await updateFincenTransferorAPI(fincenReport.fincenReportId?.toString() ?? '', transferors, token);
  }, [fincenReport, getAccessToken, transferors, updateTransferor]);

  useAutoSaveNavigation('transferor-information', handleAutoSave, fileId, fincenReport?.reportStatusCdId !== 9302);

  const { triggerSave, isSaving } = useFincenAutoSave({
    data: transferors,
    updateFunction: updateTransferor,
    apiSaveFunction: updateFincenTransferorAPI,
    reportId: fincenReport?.fincenReportId,
    hasContent: (data) => {
      if (isInitialLoadRef.current) {
        return false;
      }
      return data.some((t) => !!(t.firstName || t.entityLegalName));
    },
    enabled: fincenReport?.reportStatusCdId !== 9302,
    errorContext: 'transferor information',
  });

  useEffect(() => {
    // If we just saved, don't overwrite local state with potentially stale context data
    if (justSavedRef.current) {
      setTimeout(() => {
        justSavedRef.current = false;
      }, 2000);
      return;
    }

    if (fincenReport?.transferor) {
      const existingTransferors = Array.isArray(fincenReport.transferor)
        ? fincenReport.transferor
        : [fincenReport.transferor];

      const localStateIsEmpty =
        transferors.length === 1 && !transferors[0].firstName && !transferors[0].entityLegalName;

      const contextHasData =
        existingTransferors.length > 0 &&
        existingTransferors.some((t) => t.firstName || t.entityLegalName || t.address?.address1);

      if (localStateIsEmpty && contextHasData) {
        hasLoadedFromContextRef.current = true;
        setTransferors(existingTransferors);
        setTimeout(() => {
          isInitialLoadRef.current = false;
        }, 500);
      } else if (!hasLoadedFromContextRef.current && !localStateIsEmpty) {
        hasLoadedFromContextRef.current = true;
        isInitialLoadRef.current = false;
      } else {
        isInitialLoadRef.current = false;
      }
    } else {
      isInitialLoadRef.current = false;
    }
  }, [fincenReport?.transferor]);

  const updateTransferorData = async (): Promise<any> => {
    const token = await getAccessToken();
    return updateFincenTransferorAPI(fincenReport?.fincenReportId?.toString() ?? '', transferors, token);
  };

  const {
    execute: executeUpdateTransferorData,
    value: updateTransferorResponse,
    status: updateTransferorStatus,
    errors: updateTransferorErrors,
  } = useAsync<any>(updateTransferorData, false);

  useEffect(() => {
    if (formSubmitted) {
      executeUpdateTransferorData();
    }
  }, [formSubmitted]);

  useEffect(() => {
    if (updateTransferorStatus === AsyncStatus.SUCCESS) {
      if (shouldNavigateOnComplete) {
        setShouldNavigateOnComplete(false);
        history.push(`/files/${fileId}/fincen-reporting/payment-info`);
      } else {
        addSnackbarMessage({
          message: 'Saving Successful',
          type: 'success',
        });
      }
      // Reload the fincen report to refresh data from server
      fincenReportCtx?.reloadFincenReport?.(fileId);
      setFormSubmitted(false);
    }
  }, [updateTransferorStatus, shouldNavigateOnComplete]);

  const handleSubmissionComplete = (forceNavigate?: boolean) => {
    setFormSubmitted(false);
    if (forceNavigate || shouldNavigateOnComplete) {
      setShouldNavigateOnComplete(false);
      history.push(`/files/${fileId}/fincen-reporting/payment-info`);
    }
  };

  const handleAddTransferor = () => {
    if (transferors.length >= MAX_ITEMS_LIMIT) {
      return;
    }
    const newTransferor = {
      ...getInitialTransferorData(),
      id: `transferor_${Date.now()}`,
    } as Transferor;
    setTransferors([...transferors, newTransferor]);
  };

  const handleUpdateTransferor = (transferor: Transferor, index: number) => {
    const updatedTransferors = [...transferors];
    updatedTransferors[index] = transferor;
    setTransferors(updatedTransferors);
  };

  const handleDeleteTransferor = (index: number) => {
    const updatedTransferors = transferors.filter((_, i) => i !== index);
    setTransferors(updatedTransferors);
  };

  const handleSaveReport = async () => {
    justSavedRef.current = true;
    setFormSubmitted(true);
    if (updateTransferor) {
      updateTransferor(transferors);
    }
  };

  const handleNext = () => {
    if (fincenReport?.reportStatusCdId === 9302) {
      history.push(`/files/${fileId}/fincen-reporting/payment-info`);
      return;
    }
    justSavedRef.current = true;
    if (updateTransferor) {
      updateTransferor(transferors);
    }
    setShouldNavigateOnComplete(true);
    setFormSubmitted(true);
  };

  const handleBack = () => {
    history.push(`/files/${fileId}/fincen-reporting/transferee-info`);
  };

  // Redirect to files if fileId is not present (handles browser refresh edge case)
  if (!fileId) {
    return <Redirect to="/files" />;
  }

  return (
    <>
      <LoadingSpinner
        status={
          updateTransferorStatus === AsyncStatus.PENDING || isNavigating || fincenReportStatus === 'pending'
            ? 'pending'
            : 'success'
        }
        variant="linear"
      />
      <TransferorInformationContent
        transferors={transferors}
        onAddTransferor={handleAddTransferor}
        onUpdateTransferor={handleUpdateTransferor}
        onDeleteTransferor={handleDeleteTransferor}
        isReadOnly={fincenReport?.reportStatusCdId === 9302}
      />
      <BottomAppBar
        cancelLabel="Back"
        onCancel={handleBack}
        cancelDataQa="fincen-transferor-back-button"
        secondaryActionLabel="Save Report"
        onSecondaryClick={handleSaveReport}
        secondaryActionDataQa="fincen-transferor-save-button"
        primaryActionLabel="Next"
        onPrimaryClick={handleNext}
        primaryActionDataQa="fincen-transferor-next-button"
        disableSecondary={fincenReport?.reportStatusCdId === 9302}
      />
    </>
  );
};

export default TransferorInformationContainer;
